import type { StatusTone } from "~/features/automation/status";

/**
 * `statusDetail` — the server's person-facing projection of a dispatch's state, and the one
 * the surfaces render.
 *
 * It exists as a separate vocabulary from `status` because three of its words have no
 * equivalent in the lifecycle enum at all: a row parked on the IDE's consent prompt is
 * `Claimed`, a row aimed at a window that never came back is `Queued`, and a refusal from the
 * person at the keyboard is `Failed`. Collapsing them into `status` would render three very
 * different situations as the same badge.
 *
 * The eight words are closed; anything else falls through to a NEUTRAL pill carrying the raw
 * word (§E-6). A server that grows a ninth must show it, never blank the row.
 */
export interface DispatchStatusPresentation {
  label: string;
  tone: StatusTone;
  /** The sentence under the badge, where one word is not the whole answer. */
  note?: string;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function dispatchStatusPresentation(
  statusDetail: string | null,
  t: Translate,
  context: { queuedBehind?: number; ownerUserName?: string | null } = {},
): DispatchStatusPresentation {
  switch (statusDetail) {
    case "queued":
      return {
        label: t("Queued"),
        tone: "neutral",
        ...(context.queuedBehind && context.queuedBehind > 0
          ? { note: t("{{count}} ahead of it on that window", { count: context.queuedBehind }) }
          : {}),
      };
    case "unreachable-window":
      // Not a failure of the work — a failure to find anywhere to do it.
      return {
        label: t("No window"),
        tone: "danger",
        note: t("No online window matches where this was aimed."),
      };
    case "awaiting-approval":
      // The window CLAIMED it and is asking the person at that keyboard. There is nothing to
      // press here: the answer happens in the IDE Inbox, on their machine.
      return {
        label: t("Awaiting approval"),
        tone: "warning",
        note: t("The person at that window is being asked whether to run it."),
      };
    case "running":
      return { label: t("Running"), tone: "accent" };
    case "completed":
      return { label: t("Completed"), tone: "success" };
    case "failed":
      return { label: t("Failed"), tone: "danger" };
    case "expired":
      return {
        label: t("Expired"),
        tone: "warning",
        note: t("No window claimed it in time."),
      };
    case "cancelled":
      return { label: t("Cancelled"), tone: "neutral" };
    case "declined":
      // `denied_by_policy` — either the human said no, or an `ask` window timed out, which
      // DENIES. Both are a refusal, and neither is an error to be retried blindly.
      return {
        label: t("Declined"),
        tone: "warning",
        note: t("The window refused it, or the request timed out waiting for an answer."),
      };
    default:
      return { label: statusDetail ?? t("Unknown"), tone: "neutral" };
  }
}

/** The three words that mean "this can still change on its own". */
export function isDispatchDetailLive(statusDetail: string | null): boolean {
  return (
    statusDetail === "queued" || statusDetail === "running" || statusDetail === "awaiting-approval"
  );
}
